/**
 * useNavigationFilter - Hook for ABAC-based navigation filtering
 *
 * Filters navigation routes based on user permissions, replacing legacy role-based filtering.
 * Uses capabilities endpoint for efficient permission loading when available.
 * Falls back to legacy role/plan checks when ABAC properties are not present.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAppMode, VENDOR_ROLES } from "config/appMode";
import {
  selectUserContext,
  selectPermissionCache,
  selectCapabilities,
  selectCapabilitiesLoading,
} from "redux-store/abac/selectors";
import { evaluateBatch, loadCapabilities } from "redux-store/abac/actions";
import { loadEntitlements } from "redux-store/entitlements/actions";
import {
  selectEntitlementsLoaded,
  selectEntitlementsLoading,
  selectLaneKeys,
} from "redux-store/entitlements/selectors";
import abacCache from "utils/abacCache";

/**
 * Hook for filtering navigation items based on ABAC permissions
 *
 * @param {Array} routes - Array of route objects from routes.js
 * @param {Object} options - Options
 * @param {boolean} [options.useLegacyFallback=true] - Fall back to role/plan checks if no ABAC config
 *
 * @returns {Object} Filtered routes and state
 */
export const useNavigationFilter = (routes, options = {}) => {
  const { useLegacyFallback = true, useCapabilities = true } = options;

  const dispatch = useDispatch();
  const userContext = useSelector(selectUserContext);
  const permissionCache = useSelector(selectPermissionCache);
  const capabilities = useSelector(selectCapabilities);
  const capabilitiesLoading = useSelector(selectCapabilitiesLoading);
  const { user } = useSelector((state) => state.auth);
  // Plan K Phase 2: lane bundle filtering. A route with a `laneKey` only
  // renders when the org has that bundle (derived from its feature keys). If
  // entitlements have not loaded yet we pass — the slice's `loaded` state
  // changes will re-trigger this hook via the userContext dep.
  const entitlementLaneKeys = useSelector(selectLaneKeys);
  const entitlementsLoaded = useSelector(selectEntitlementsLoaded);
  const entitlementsLoading = useSelector(selectEntitlementsLoading);

  // Kick off the one-shot entitlements load as soon as we have an authenticated
  // user — the sidenav needs the lane keys to decide what to show.
  useEffect(() => {
    if (!user) return;
    if (entitlementsLoaded || entitlementsLoading) return;
    dispatch(loadEntitlements());
  }, [user, entitlementsLoaded, entitlementsLoading, dispatch]);

  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionResults, setPermissionResults] = useState({});

  // Extract user info for legacy fallback
  const userRole = useMemo(() => {
    let role = user?.userDetails?.role;
    const planType = user?.subscription?.subscriptionPlan?.planType;
    const isPartner = Number(planType) === 2;

    // Super Admin should never be overridden
    if (role === "SUPER_ADMIN") {
      return role;
    }

    // Pre-CollapsePrimaryRoles (2026-06-11) this passed VENDOR /
    // RECYCLING_VENDOR / MAINTENANCE_VENDOR through unchanged so the
    // /vendor/dashboard route matcher saw them. After the collapse no
    // user holds those primary roles, so VENDOR_ROLES is an empty Set
    // and the gate is dead code — left for grep clarity.
    if (VENDOR_ROLES.has(role?.toUpperCase())) {
      return role;
    }

    if (role !== "OWNER") {
      role = "Employee";
    }

    if (isPartner) {
      role = "Partner";
    }

    return role;
  }, [user]);

  const planId = user?.subscription?.subscriptionPlan?.id;
  const isSuperAdmin = userRole === "SUPER_ADMIN";

  // Check if user's org is a vendor organization
  const isVendorOrg = useMemo(() => {
    return user?.userDetails?.isVendorOrganization === true;
  }, [user]);

  // Can the org open the vendor-side pages? Flagged as a vendor, or flex2-api
  // reports it serves customers (its Command Center was turned on in the
  // backoffice; Jam 0c74b41e). hideForVendorOrg still keys off the flag
  // alone, so such an org keeps its client-side pages too.
  const worksVendorSide = useMemo(() => {
    return isVendorOrg || user?.userDetails?.servesCustomers === true;
  }, [isVendorOrg, user]);

  /**
   * Check if route passes legacy role/plan filtering
   */
  const checkLegacyPermission = useCallback(
    (route) => {
      // Super Admin sees every route regardless of legacy role/plan tags.
      if (isSuperAdmin) {
        return true;
      }

      const { roles = null, plans = null } = route;

      // Check role requirement. Pre-CollapsePrimaryRoles, vendor-portal
      // routes carried `roles: ["VENDOR" | "RECYCLING_VENDOR" | ...]`;
      // the second condition let any USER in a vendor org see them.
      // After the collapse the role-string match always fails (no user
      // has those primary roles), so we now grant access purely on
      // "the route lists vendor-portal roles AND the caller's org is a
      // vendor". The actual vendor sub-type discrimination moves to
      // page-level org-vendor-types checks.
      if (roles && !roles.includes(userRole)) {
        const isVendorRoute = roles.some((r) =>
          ["VENDOR", "RECYCLING_VENDOR", "MAINTENANCE_VENDOR"].includes(r),
        );
        if (!isVendorRoute || !worksVendorSide) {
          return false;
        }
      }

      // Check plan requirement
      if (!planId) {
        return false;
      }

      return true;
    },
    [userRole, planId, isSuperAdmin, worksVendorSide]
  );

  /**
   * Check if route has ABAC configuration
   */
  const hasAbacConfig = useCallback((route) => {
    return route.abacResource && route.abacAction;
  }, []);

  /**
   * Check capability from capabilities endpoint
   */
  const checkCapability = useCallback(
    (resourceType, action) => {
      if (!capabilities?.actions) return null;

      // Check if capabilities cache is still valid (5 minutes)
      const now = Date.now();
      const ttl = 5 * 60 * 1000;
      if (capabilities.timestamp && now - capabilities.timestamp > ttl) {
        return null; // Capabilities expired
      }

      // Capabilities actions are in format "resource.action" (lowercase)
      const normalizedKey = `${resourceType.toLowerCase()}.${action.toLowerCase()}`;
      return capabilities.actions.includes(normalizedKey);
    },
    [capabilities]
  );

  /**
   * Get cached permission result
   */
  const getCachedPermission = useCallback(
    (resourceType, action) => {
      // First check capabilities if enabled and available
      if (useCapabilities && capabilities) {
        const capabilityResult = checkCapability(resourceType, action);
        if (capabilityResult !== null) {
          return capabilityResult;
        }
      }

      const cacheKey = abacCache.constructor.generateKey(resourceType, action);

      // Check LRU cache first
      const lruCached = abacCache.get(cacheKey);
      if (lruCached !== null) {
        return lruCached.granted === true;
      }

      // Check Redux cache
      const reduxCached = permissionCache[cacheKey];
      if (reduxCached) {
        const now = Date.now();
        const ttl = 5 * 60 * 1000; // 5 minutes

        if (now - reduxCached.timestamp <= ttl) {
          return reduxCached.granted === true;
        }
      }

      return null; // Not cached
    },
    [permissionCache, capabilities, useCapabilities, checkCapability]
  );

  /**
   * Collect all unique ABAC permissions needed for routes
   */
  const collectPermissions = useCallback(
    (routeList) => {
      const permissions = new Map();

      const processRoute = (route) => {
        if (hasAbacConfig(route)) {
          const key = `${route.abacResource}:${route.abacAction}`;
          if (!permissions.has(key)) {
            permissions.set(key, {
              resourceType: route.abacResource,
              action: route.abacAction,
            });
          }
        }

        // Process nested collapse items
        if (route.collapse && Array.isArray(route.collapse)) {
          route.collapse.forEach(processRoute);
        }
      };

      routeList.forEach(processRoute);

      return Array.from(permissions.values());
    },
    [hasAbacConfig]
  );

  /**
   * Evaluate permissions - uses capabilities endpoint when available
   */
  const evaluatePermissions = useCallback(async () => {
    if (!routes || routes.length === 0) {
      setFilteredRoutes([]);
      setLoading(false);
      return;
    }

    // Super Admin bypass - grant all permissions without API calls
    if (isSuperAdmin) {
      const permissionsNeeded = collectPermissions(routes);
      const results = {};

      permissionsNeeded.forEach((perm) => {
        const key = `${perm.resourceType}:${perm.action}`;
        results[key] = true; // Grant all permissions
      });

      setPermissionResults(results);
      setLoading(false);
      return;
    }

    const permissionsNeeded = collectPermissions(routes);
    const results = {};

    // Try to load capabilities if enabled and not already loaded/loading
    if (useCapabilities && !capabilities && !capabilitiesLoading && userContext) {
      try {
        await dispatch(loadCapabilities()).unwrap();
        // After loading capabilities, all permissions will be resolved from cache
      } catch (error) {
        console.error("Failed to load capabilities, falling back to batch:", error);
      }
    }

    // First, check cache for all permissions (including from capabilities)
    const uncachedPermissions = [];
    permissionsNeeded.forEach((perm) => {
      const cached = getCachedPermission(perm.resourceType, perm.action);
      const key = `${perm.resourceType}:${perm.action}`;

      if (cached !== null) {
        results[key] = cached;
      } else {
        uncachedPermissions.push(perm);
      }
    });

    // Batch evaluate any uncached permissions (fallback if capabilities didn't cover them)
    if (uncachedPermissions.length > 0 && userContext) {
      try {
        const batchResult = await dispatch(evaluateBatch(uncachedPermissions)).unwrap();

        if (batchResult && batchResult.results) {
          batchResult.results.forEach((result, index) => {
            const perm = uncachedPermissions[index];
            const key = `${perm.resourceType}:${perm.action}`;
            results[key] = result.granted === true;

            // Update LRU cache
            const cacheKey = abacCache.constructor.generateKey(perm.resourceType, perm.action);
            abacCache.set(cacheKey, result);
          });
        }
      } catch (error) {
        console.error("Failed to batch evaluate permissions:", error);
        // On error, grant access for Super Admin, deny for others
        uncachedPermissions.forEach((perm) => {
          const key = `${perm.resourceType}:${perm.action}`;
          results[key] = isSuperAdmin;
        });
      }
    }

    setPermissionResults(results);
  }, [routes, collectPermissions, getCachedPermission, userContext, dispatch, isSuperAdmin, useCapabilities, capabilities, capabilitiesLoading]);

  /**
   * Check if route should be visible
   */
  const shouldShowRoute = useCallback(
    (route) => {
      // Vendor org conditional visibility
      if (route.vendorOrgOnly && !worksVendorSide) return false;
      if (route.hideForVendorOrg && isVendorOrg) return false;

      // `route.vendorRoles` historically said "only show if the user's
      // primary role is one of these vendor sub-types". With the
      // primary roles collapsed (2026-06-11), every user is USER, so
      // the legacy check would hide the route from everyone. The new
      // semantic: a route with `vendorRoles` is for vendor orgs in
      // general; org-vendor-type discrimination happens on the
      // receiving page if needed.
      if (route.vendorRoles?.length > 0 && !worksVendorSide) return false;

      // requiresSecondaryRole — the post-CollapsePrimaryRoles gate. A
      // route with `requiresSecondaryRole: "service_manager"` (or any
      // OrganizationRoleScopeMembers scopeKind) only renders when the
      // user holds that secondary scope OR is the org's OWNER (OWNER
      // inherits every secondary capability by definition) OR is a
      // SUPER_ADMIN (platform admins bypass everything).
      const requiredScope = route.requiresSecondaryRole;
      if (requiredScope) {
        if (!isSuperAdmin && user?.userDetails?.role !== "OWNER") {
          const scopes = user?.userDetails?.secondaryRoles || [];
          const hasScope = Array.isArray(scopes)
            ? scopes.some(
                (s) =>
                  s === requiredScope ||
                  s?.scopeKind === requiredScope ||
                  s?.kind === requiredScope,
              )
            : false;
          if (!hasScope) return false;
        }
      }

      // Filter by app mode if route has appMode tag. Pass the whole userDetails
      // so getAppMode can use the server-computed effectiveAppMode (which folds
      // in org-systems + the user's preference) instead of role alone.
      if (route.appMode) {
        const currentMode = getAppMode(user?.userDetails);
        if (route.appMode !== currentMode) return false;
      }

      // Plan K Phase 2 — lane bundle gating. A route with `laneKey` only
      // appears if the org has the bundle. Super Admin always sees every lane
      // (matches the existing ABAC bypass). Until entitlements load we err on
      // the side of showing — the slice flips `loaded` after the initial
      // fetch and the hook re-runs.
      if (route.laneKey && !isSuperAdmin && entitlementsLoaded) {
        if (!entitlementLaneKeys.includes(route.laneKey)) return false;
      }

      // Always show non-collapse items (titles, dividers) that passed appMode check
      if (route.type === "title" || route.type === "divider") {
        return true;
      }

      // Check ABAC permission if configured
      if (hasAbacConfig(route)) {
        const key = `${route.abacResource}:${route.abacAction}`;
        const granted = permissionResults[key];

        if (granted === true) {
          return true;
        }

        if (granted === false) {
          // If ABAC denies but legacy fallback is enabled, check legacy
          if (useLegacyFallback) {
            return checkLegacyPermission(route);
          }
          return false;
        }

        // Permission not yet evaluated - use legacy check
        if (useLegacyFallback) {
          return checkLegacyPermission(route);
        }

        return false;
      }

      // No ABAC config - use legacy filtering
      if (useLegacyFallback) {
        return checkLegacyPermission(route);
      }

      return true;
    },
    [
      hasAbacConfig,
      permissionResults,
      useLegacyFallback,
      checkLegacyPermission,
      isVendorOrg,
      worksVendorSide,
      isSuperAdmin,
      entitlementsLoaded,
      entitlementLaneKeys,
    ]
  );

  /**
   * Filter route and its children
   */
  const filterRoute = useCallback(
    (route) => {
      if (!shouldShowRoute(route)) {
        return null;
      }

      // Filter nested collapse items
      if (route.collapse && Array.isArray(route.collapse)) {
        const filteredCollapse = route.collapse
          .map((child) => filterRoute(child))
          .filter(Boolean);

        // Don't show parent if all children are filtered out
        if (filteredCollapse.length === 0 && route.type === "collapse") {
          return null;
        }

        return {
          ...route,
          collapse: filteredCollapse,
        };
      }

      return route;
    },
    [shouldShowRoute]
  );

  /**
   * Apply filtering to all routes
   */
  const applyFiltering = useCallback(() => {
    const filtered = routes.map((route) => filterRoute(route)).filter(Boolean);

    // Strip title items that have no following non-title items
    const result = filtered.filter((route, index) => {
      if (route.type !== "title") return true;
      for (let i = index + 1; i < filtered.length; i++) {
        if (filtered[i].type === "title") return false;
        if (filtered[i].type !== "divider") return true;
      }
      return false;
    });

    setFilteredRoutes(result);
    setLoading(false);
  }, [routes, filterRoute]);

  // Evaluate permissions when routes, user context, capabilities, or
  // super-admin state change. The super-admin grant lives inside
  // evaluatePermissions, so isSuperAdmin must be in the dep list — otherwise
  // a late-loading user (typical) leaves permissionResults empty forever.
  useEffect(() => {
    setLoading(true);
    evaluatePermissions();
  }, [routes, userContext, capabilities, isSuperAdmin]);

  // Apply filtering when permission results change OR entitlements finish
  // loading (lane gates depend on entitlement state).
  useEffect(() => {
    applyFiltering();
  }, [permissionResults, applyFiltering, entitlementsLoaded, entitlementLaneKeys]);

  // Also refilter when legacy user data changes
  useEffect(() => {
    if (useLegacyFallback) {
      applyFiltering();
    }
  }, [userRole, planId, useLegacyFallback, applyFiltering]);

  return {
    routes: filteredRoutes,
    loading,
    permissionResults,
    // Helper methods
    hasPermission: (resourceType, action) => {
      const key = `${resourceType}:${action}`;
      return permissionResults[key] === true;
    },
    refreshPermissions: evaluatePermissions,
  };
};

export default useNavigationFilter;
